import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { hashKey, useQuery, type QueryKey } from "@tanstack/react-query";
import {
  invalidateScopedPages, queryClient, resetScopedPages,
  SCOPED_PAGE_GC_MS, SCOPED_PAGE_STALE_MS,
} from "../../../lib/queryClient";
import { authStore } from "../../../lib/auth/authStore";

import {
  ForbiddenError,
  ScopeSealedOrMissingError,
  SessionExpiredError,
  classifyFetchError,
} from "../../state/denialErrors";

/**
 * The CURRENT node the page is scoped to = the DEEPEST present URL scope segment
 * (project → engagement → client → workspace). B4: a section page passes THIS as its
 * reader/requester scope id, so it shows the node you're standing in — not a hardcoded
 * ancestor. The server wall filters by this id; a deeper id is more restrictive, never a bypass.
 */
export function useScopeNodeId(): string | undefined {
  const { ws, client, eng, project, node } = useParams({ strict: false }) as {
    ws?: string; client?: string; eng?: string; project?: string; node?: string;
  };
  // `node` = the generic /w/$ws/n/$node family (spec 48.24 §6): a node addressed by id, free-shape
  // safe. When present it IS the current node (wins over the positional typed segments, which stay
  // as deep-link aliases). Otherwise fall back to the deepest present typed segment.
  return node || project || eng || client || ws;
}

/** The workspace slug in the current route. Kept URL-derived so a stale context snapshot cannot
 * authorize a client node beneath a different workspace during navigation. */
export function useWorkspaceScopeId(): string | undefined {
  const { ws } = useParams({ strict: false }) as { ws?: string };
  return ws;
}

/**
 * Shared loader for the scope-aware destination pages (Data / Activity / Decisions /
 * Engagements / Intel / Governance). Centralises the loading→ready/error + cancel-on-unmount
 * lifecycle so each page only declares its real fetch — no stubs, honest error/empty states.
 * Mirrors the proven MyWorkPage / LibraryView fetch shape (apiBase + fetchImpl injectable).
 */
export type ScopedState<T> =
  | { kind: "loading" }
  // `reason`/`code` are populated when the failure classifies as a denial (ForbiddenError /
  // ScopeSealedOrMissingError) so a renderer can route to ShellForbidden instead of a plain alert;
  // absent for an unclassified failure (network/500/422), which stays a plain error message.
  | { kind: "error"; message: string; reason?: "forbidden" | "sealed"; code?: string }
  | { kind: "ready"; data: T; refreshing?: boolean };

type ScopedSnapshot<T> = {
  deps: readonly unknown[];
  request: object | null;
  state: ScopedState<T>;
};

function dependenciesMatch(left: readonly unknown[], right: readonly unknown[]): boolean {
  return left.length === right.length && left.every((value, index) => Object.is(value, right[index]));
}

export interface ScopedFetchCache {
  /** Explicit resource identity, including workspace/scope and every server-side filter. */
  key: QueryKey;
  /** Mutation/manual-refresh counter; refreshes the same entry rather than retaining old revisions. */
  revision?: number;
}

function errorState<T>(error: unknown): ScopedState<T> {
  if (error instanceof ForbiddenError) {
    return { kind: "error", message: error.message, reason: "forbidden", code: error.code };
  }
  if (error instanceof ScopeSealedOrMissingError) {
    return { kind: "error", message: error.message, reason: "sealed" };
  }
  return { kind: "error", message: error instanceof Error ? error.message : String(error) };
}

export function useScopedFetch<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  cache?: ScopedFetchCache,
): ScopedState<T> {
  // Anonymous/test-only reads retain the original lifecycle. Cache data is memory-only and
  // partitioned by authenticated actor as well as the caller's explicit resource key.
  const cached = cache !== undefined && authStore.cacheIdentity !== null;
  const queryKey = ["scoped-page", authStore.cacheIdentity, ...(cache?.key ?? ["uncached"])];
  const keyHash = hashKey(queryKey);
  const query = useQuery({
    queryKey,
    queryFn: () => loader(new AbortController().signal),
    enabled: cached,
    retry: false,
    staleTime: SCOPED_PAGE_STALE_MS,
    gcTime: SCOPED_PAGE_GC_MS,
  }, queryClient);
  // The cache owns read requests across navigation/StrictMode remounts. Deliberately do not
  // consume TanStack's signal: a departing reader must not abort a shared/background read.
  // Clearing the QueryClient on identity change still cancels the query and discards late data.
  const revisionRef = useRef({ keyHash, revision: cache?.revision });
  useEffect(() => {
    const previous = revisionRef.current;
    revisionRef.current = { keyHash, revision: cache?.revision };
    if (cached && previous.keyHash === keyHash && previous.revision !== cache?.revision) {
      void queryClient.invalidateQueries({ queryKey, exact: true });
    }
  }, [cached, keyHash, cache?.revision]);
  const [snapshot, setSnapshot] = useState<ScopedSnapshot<T>>(() => ({
    deps: [...deps],
    request: null,
    state: { kind: "loading" },
  }));
  const navigate = useNavigate();
  useEffect(() => {
    if (!cached || !query.isError) return;
    if (!(query.error instanceof ForbiddenError
      || query.error instanceof ScopeSealedOrMissingError
      || query.error instanceof SessionExpiredError)) return;
    // Denials must evict previously allowed data. Keep ordinary failures settled:
    // removing an active query recreates it on the next render and restarts its GET.
    queryClient.removeQueries({ queryKey, exact: true });
    if (query.error instanceof SessionExpiredError) navigate({ to: "/login" });
  }, [cached, keyHash, query.isError, query.error, navigate]);
  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    const controller = new AbortController();
    const request = {};
    const requestDeps = [...deps];
    const publish = (state: ScopedState<T>) => {
      if (cancelled) return;
      setSnapshot((current) => (
        current.request === request ? { deps: requestDeps, request, state } : current
      ));
    };

    setSnapshot({ deps: requestDeps, request, state: { kind: "loading" } });
    loader(controller.signal)
      .then((data) => {
        publish({ kind: "ready", data });
      })
      .catch((e) => {
        if (cancelled) return;
        // Session-expired is not a permission denial — re-establish the session rather than
        // render a denial state for the wrong cause.
        if (e instanceof SessionExpiredError) {
          navigate({ to: "/login" });
          return;
        }
        if (e instanceof ForbiddenError) {
          publish({ kind: "error", message: e.message, reason: "forbidden", code: e.code });
          return;
        }
        if (e instanceof ScopeSealedOrMissingError) {
          publish({ kind: "error", message: e.message, reason: "sealed" });
          return;
        }
        publish({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // loader is intentionally re-created per render; the explicit `deps` array is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, cached]);

  const cachedState = useMemo<ScopedState<T>>(() => {
    if (query.isError) return errorState(query.error);
    if (query.data !== undefined) {
      return query.isFetching
        ? { kind: "ready", data: query.data, refreshing: true }
        : { kind: "ready", data: query.data };
    }
    return { kind: "loading" };
  }, [query.isError, query.error, query.data, query.isFetching]);
  if (cached) return cachedState;

  // Effects reset state after commit. This render-time key check also prevents a terminal state
  // from the previous scope/request being observable during the first render for new dependencies.
  return dependenciesMatch(snapshot.deps, deps) ? snapshot.state : { kind: "loading" };
}

/**
 * GET JSON through the shell's single denial-mapping layer — the fetch primitive these pages
 * share. 401/403/404 throw the shell's typed denial errors (see denialErrors.ts); any other
 * non-ok status still throws a plain Error carrying the raw HTTP status, unchanged.
 */
export async function getJson<T>(
  base: string,
  path: string,
  fetchImpl?: typeof fetch,
  signal?: AbortSignal,
): Promise<T> {
  const f = fetchImpl ?? fetch;
  const init: RequestInit = { method: "GET" };
  if (signal) init.signal = signal;
  const res = await f(`${base.replace(/\/$/, "")}${path}`, init);
  if (!res.ok) throw await classifyFetchError(res, `getJson ${path}`);
  return (await res.json()) as T;
}

async function scopedWriteError(response: Response, operation: string): Promise<Error> {
  const error = await classifyFetchError(response, operation);
  if (error instanceof SessionExpiredError || error instanceof ForbiddenError || error instanceof ScopeSealedOrMissingError) {
    // A write can reveal revoked access before the next GET. Discard previous read results now,
    // so revisiting the page cannot revive them inside the normal freshness window.
    resetScopedPages();
  }
  return error;
}

/** POST a JSON body and return typed JSON through the same denial-mapping layer as getJson. */
export async function postJson<T>(base: string, path: string, body: unknown, fetchImpl?: typeof fetch): Promise<T> {
  const f = fetchImpl ?? fetch;
  const res = await f(`${base.replace(/\/$/, "")}${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    credentials: "include",
  });
  if (!res.ok) throw await scopedWriteError(res, `postJson ${path}`);
  invalidateScopedPages("none");
  return (await res.json()) as T;
}

/** PUT a JSON body through the same denial-mapping boundary as other scoped mutations. */
export async function putJson<T>(base: string, path: string, body: unknown, fetchImpl?: typeof fetch): Promise<T> {
  const f = fetchImpl ?? fetch;
  const res = await f(`${base.replace(/\/$/, "")}${path}`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    credentials: "include",
  });
  if (!res.ok) throw await scopedWriteError(res, `putJson ${path}`);
  invalidateScopedPages("none");
  return (await res.json()) as T;
}

/** PATCH a JSON body through the same denial-mapping boundary as the other scoped mutations. */
export async function patchJson<T>(base: string, path: string, body: unknown, fetchImpl?: typeof fetch): Promise<T> {
  const f = fetchImpl ?? fetch;
  const res = await f(`${base.replace(/\/$/, "")}${path}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    credentials: "include",
  });
  if (!res.ok) throw await scopedWriteError(res, `patchJson ${path}`);
  invalidateScopedPages("none");
  return (await res.json()) as T;
}
